import { FC, useEffect, useRef, useState } from "react";
import { IonButton } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { HalfEvent, VideoPlaySegment } from "refsix-js-models";
import mapEventNameToDisplayName from "../../../utils/MapEventToDisplayName";
import { VideoDetails } from "../replayTypes";
import { secondsToClock } from "../replayUtils";
import VideoRequestFile from "./videoRequestFile";

interface VideoCalibrateProps {
    matchTimings: HalfEvent[];
    videoDetails: VideoDetails;
    onCalibrated: (videoDetails: VideoDetails) => void;
    onRequestFile: () => void;
}

/**
 * Lets the user mark the kick-off point of each playing half within the loaded
 * video so playback can be synced to the match timeline. Ported from the legacy
 * VideoCalibrateDirective.
 */
const VideoCalibrate: FC<VideoCalibrateProps> = ({
    matchTimings,
    videoDetails,
    onCalibrated,
    onRequestFile,
}) => {
    const { t } = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playSegments, setPlaySegments] = useState<VideoPlaySegment[]>(
        videoDetails.playSegmentOffsets || []
    );

    const hasVideo = !!videoDetails.url;

    // Load the selected video into the calibration player.
    useEffect(() => {
        const video = videoRef.current;
        if (video && videoDetails.url && !videoDetails.isCalibrated) {
            video.src = videoDetails.url;
            video.play().catch((err) =>
                console.warn("Replay: calibration video play failed", err)
            );
            setPlaySegments(videoDetails.playSegmentOffsets || []);
        }
    }, [videoDetails.url, videoDetails.isCalibrated]);

    const setOffset = (index: number) => {
        const video = videoRef.current;
        const timing = matchTimings[index];
        if (!video || !timing) {
            return;
        }
        const next = [...playSegments];
        next[index] = {
            offsetSec: Math.floor(video.currentTime),
            durationSec:
                ((timing.endTime || timing.timestamp) - timing.timestamp) /
                1000,
            minuteOfPlayMilli: (timing.minuteOfPlay || 0) * 60 * 1000,
        };
        setPlaySegments(next);
    };

    const finishCalibration = () => {
        onCalibrated({
            ...videoDetails,
            playSegmentOffsets: playSegments,
            isCalibrated: true,
        });
    };

    // Every half must have an offset set. filter(Boolean).length would compact
    // holes and let a sparse array (a gap at an earlier index) pass, which later
    // throws in the player, so check each index explicitly.
    const allSegmentsSet =
        matchTimings.length > 0 &&
        matchTimings.every((_, index) => playSegments[index]);

    return (
        <div className="video-calibration">
            <div className="video-calibration-panels">
                <div className="video-calibrate-instructions padding-horizontal">
                    <h2>{t("performance.VideoCalibrate.title")}</h2>
                    <p>{t("performance.VideoCalibrate.instructions")}</p>
                    <ul>
                        <li className={!hasVideo ? "active" : ""}>
                            {t("performance.VideoCalibrate.step1")}
                        </li>
                        {hasVideo &&
                            matchTimings.map((half, index) => {
                                // Only reveal a step once the previous one is set.
                                if (index !== 0 && !playSegments[index - 1]) {
                                    return null;
                                }
                                return (
                                    <li
                                        key={index}
                                        className={
                                            !playSegments[index] ? "active" : ""
                                        }
                                    >
                                        <p>
                                            {t(
                                                "performance.VideoCalibrate.stepN",
                                                {
                                                    stepNumber: index + 2,
                                                    halfName:
                                                        mapEventNameToDisplayName(
                                                            half,
                                                            t
                                                        ),
                                                }
                                            )}
                                        </p>
                                        <p className="set-start">
                                            <span className="start-time">
                                                {secondsToClock(
                                                    playSegments[index]
                                                        ?.offsetSec || 0
                                                )}
                                            </span>
                                            <IonButton
                                                size="small"
                                                onClick={() => setOffset(index)}
                                            >
                                                {t(
                                                    "performance.VideoCalibrate.markAsStart"
                                                )}
                                            </IonButton>
                                        </p>
                                    </li>
                                );
                            })}
                    </ul>
                    {hasVideo && (
                        <IonButton
                            className="finish"
                            expand="block"
                            onClick={finishCalibration}
                            disabled={!allSegmentsSet}
                        >
                            {t("performance.VideoCalibrate.finishCalibration")}
                        </IonButton>
                    )}
                </div>
                <div className="video-container">
                    <video ref={videoRef} controls />
                    {!hasVideo && (
                        <VideoRequestFile onRequestFile={onRequestFile} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideoCalibrate;
